import { useState } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db6g4f22sgkaik1unes0";

const inputClass =
  "w-full h-11 px-3 text-sm rounded-md bg-background-50 border border-background-200 focus:border-primary-400 focus:outline-none text-foreground-900 placeholder:text-foreground-400";

export default function ContactForm() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">(
    "idle",
  );
  const [formError, setFormError] = useState("");

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }
    formData.delete("website_alt");

    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      body.append(key, String(value));
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText ||
        "";
      const isOk = response.ok && parsed?.code === "OK";
      const isSpam = String(serverMsg).toLowerCase().includes("spam");

      if (!isOk || isSpam) {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试。");
        return;
      }

      setStatus("success");
      form.reset();
    } catch {
      setStatus("error");
      setFormError("网络异常，请稍后重试。");
    }
  }

  return (
    <div className="bg-background-50 rounded-lg border border-background-200 p-4 md:p-6">
      <div className="flex items-center gap-3 mb-5">
        <span className="w-1.5 h-6 rounded-full bg-accent-500" />
        <h2 className="font-heading text-xl font-bold text-foreground-950">
          联系方式与在线咨询
        </h2>
      </div>

      {status === "success" ? (
        <div className="flex flex-col items-center text-center py-8">
          <span className="w-14 h-14 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center text-3xl mb-4">
            <i className="ri-check-line" />
          </span>
          <p className="font-heading text-lg font-semibold text-foreground-950">
            提交成功，感谢你的来信！
          </p>
          <p className="mt-2 text-sm text-foreground-500">
            我们会尽快查看并回复你的反馈。
          </p>
          <button
            type="button"
            onClick={() => setStatus("idle")}
            className="mt-5 h-10 px-5 rounded-md border border-background-300 text-foreground-700 text-sm font-medium hover:border-primary-400 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            再提交一条
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} data-readdy-form className="space-y-4">
          <input
            type="text"
            name="website_alt"
            className="form-field-website_alt"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            readOnly
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="contact-name"
                className="block text-sm text-foreground-600 mb-1.5"
              >
                你的称呼
              </label>
              <input
                id="contact-name"
                name="name"
                type="text"
                required
                placeholder="请输入你的称呼"
                className={inputClass}
              />
            </div>
            <div>
              <label
                htmlFor="contact-email"
                className="block text-sm text-foreground-600 mb-1.5"
              >
                联系邮箱
              </label>
              <input
                id="contact-email"
                name="email"
                type="email"
                required
                placeholder="name@example.com"
                className={inputClass}
              />
            </div>
          </div>

          <div>
            <label
              htmlFor="contact-subject"
              className="block text-sm text-foreground-600 mb-1.5"
            >
              咨询类型
            </label>
            <select
              id="contact-subject"
              name="subject"
              className={`${inputClass} cursor-pointer`}
              defaultValue="内容反馈"
            >
              <option value="内容反馈">内容反馈</option>
              <option value="合作洽谈">合作洽谈</option>
              <option value="版权问题">版权问题</option>
              <option value="其他建议">其他建议</option>
            </select>
          </div>

          <div>
            <label
              htmlFor="contact-message"
              className="block text-sm text-foreground-600 mb-1.5"
            >
              留言内容（最多 500 字）
            </label>
            <textarea
              id="contact-message"
              name="message"
              required
              maxLength={500}
              rows={5}
              placeholder="请描述你的问题或建议…"
              className="w-full px-3 py-2.5 text-sm rounded-md bg-background-50 border border-background-200 focus:border-primary-400 focus:outline-none text-foreground-900 placeholder:text-foreground-400 resize-none"
            />
          </div>

          {status === "error" && (
            <p className="text-sm text-accent-700 bg-accent-50 border border-accent-200 rounded-md px-3 py-2">
              {formError || "提交失败，请稍后重试。"}
            </p>
          )}

          <button
            type="submit"
            disabled={status === "submitting"}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 h-11 px-8 rounded-md bg-primary-600 text-background-50 text-sm font-medium hover:bg-primary-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
          >
            {status === "submitting" ? (
              <>
                <i className="ri-loader-4-line animate-spin" />
                提交中…
              </>
            ) : (
              <>
                <i className="ri-send-plane-line" />
                提交咨询
              </>
            )}
          </button>
          <p className="text-xs text-foreground-400">
            提交即表示你同意我们通过邮件与你联系。
          </p>
        </form>
      )}
    </div>
  );
}